import React from "react";
import { Check, AlertTriangle, Flag } from "lucide-react";
import { priorityMeta, SCOUT_LABEL_COLORS } from "../../constants/scoutRequestChoices";

// One pill family, reused identically everywhere (design 1w): "light tint + strong
// same-hue text, never loud solids". Tokens from DESIGN-INVENTORY §4.

const BASE = "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold leading-[17px] whitespace-nowrap";

// A leading 5px status dot (Open/Closed use it).
const Dot = ({ color }) => (
  <span className="w-[5px] h-[5px] rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
);

export const CategoryPill = ({ category }) => {
  const youth = category === "youth";
  return (
    <span
      className={BASE}
      style={
        youth
          ? { backgroundColor: "#FEF3C7", color: "#D97706" }
          : { backgroundColor: "#E8F1FF", color: "#1B6FE0" }
      }
    >
      {youth ? "Youth" : "Professional"}
    </span>
  );
};

export const StatusPill = ({ status }) => {
  const closed = status === "closed";
  return (
    <span
      className={BASE}
      style={
        closed
          ? { backgroundColor: "#F3F4F6", color: "#6B7280" }
          : { backgroundColor: "#ECFDF5", color: "#16A34A" }
      }
    >
      <Dot color={closed ? "#6B7280" : "#16A34A"} />
      {closed ? "Closed" : "Open"}
    </span>
  );
};

export const NewPill = () => (
  <span className={BASE} style={{ backgroundColor: "#E8F1FF", color: "#1B6FE0" }}>
    New
  </span>
);

// System-computed source label (design 1w): always visible, deliberately calm.
export const SourceLabelPill = ({ label }) => {
  const agency = label === "agency_player";
  return (
    <span
      className={BASE}
      style={
        agency
          ? { backgroundColor: "#E8F1FF", color: "#1B6FE0" }
          : { backgroundColor: "#F3F4F6", color: "#6B7280" }
      }
    >
      {agency ? "Agency player" : "Not at this agency"}
    </span>
  );
};

// Honest "Outside fee frame" pill (design 2d) — warning tint, calm wording. Only
// render when outside_fee_frame === true; null/false → no pill.
export const OutsideFramePill = () => (
  <span className={BASE} style={{ backgroundColor: "#FEF3C7", color: "#D97706" }}>
    <AlertTriangle className="w-3 h-3" />
    Outside fee frame
  </span>
);

// The ONLY agent-facing feedback in the whole feature; on the club side it marks
// a recommendation the club has moved onto the radar.
export const OnRadarPill = ({ children = "On your radar" }) => (
  <span className={BASE} style={{ backgroundColor: "#ECFDF5", color: "#16A34A" }}>
    <Check className="w-3 h-3" />
    {children}
  </span>
);

// Calm grey attribute pill for profile facts (foot, height, fee frame, loan).
export const AttributePill = ({ children }) => (
  <span
    className="inline-flex items-center gap-1 rounded-full bg-[#F3F4F6] px-2.5 py-0.5 text-[11.5px] font-medium leading-[17px] text-[#374151] whitespace-nowrap"
  >
    {children}
  </span>
);

// Agency-internal priority pill (design 5b/6a): flag icon + "Prio N" in the tier's
// tint. Agency-only — the club never sees it (Invariant 2). Renders nothing for a
// request the agency has not prioritised.
export const PriorityPill = ({ priority }) => {
  const meta = priorityMeta(priority);
  if (!meta) return null;
  return (
    <span className={BASE} style={{ backgroundColor: meta.bg, color: meta.color }}>
      <Flag className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

// Custom agency label pill (design 6a): a coloured dot + the label name, in the
// label's own hue. Agency-internal, shared across the team; never club-visible.
export const LabelPill = ({ label }) => {
  if (!label) return null;
  const hex = SCOUT_LABEL_COLORS[label.color] || SCOUT_LABEL_COLORS.blue;
  return (
    <span className={BASE} style={{ backgroundColor: "#F3F4F6", color: "#374151" }}>
      <Dot color={hex} />
      {label.name}
    </span>
  );
};

export default {
  CategoryPill,
  StatusPill,
  NewPill,
  SourceLabelPill,
  OutsideFramePill,
  OnRadarPill,
  AttributePill,
  PriorityPill,
  LabelPill,
};
